// Pre-fill a hardware request from a Product Catalogue pick.
//
// The catalogue keeps its suppliers as free-text names with a price each
// (product_catalog_suppliers), while a hardware request wants either a
// supplier record id or a proposed free-text name. This works out which
// catalogue supplier to use and maps it onto whichever of those the form can
// take, so the person raising the request is not retyping figures the PMO
// already maintains.
import { orderLink } from './howToOrder.js';

// "Midwich Ltd", "midwich" and "MIDWICH LIMITED" are the same supplier.
export const normaliseName = (name) => String(name || '')
  .toLowerCase()
  .replace(/[^a-z0-9 ]+/g, ' ')
  .replace(/\b(ltd|limited|plc|llc|inc)\b/g, '')
  .replace(/\s+/g, ' ')
  .trim();

const money = (n) => (parseFloat(n) > 0 ? String(parseFloat(n)) : '');

// Preferred supplier first; otherwise the cheapest one that has a price;
// otherwise whatever is listed first.
export function chooseCatalogueSupplier(catalogueSuppliers) {
  const list = Array.isArray(catalogueSuppliers) ? catalogueSuppliers : [];
  if (list.length === 0) return null;
  const preferred = list.find(s => s.is_preferred);
  if (preferred) return preferred;
  const priced = list.filter(s => parseFloat(s.unit_price) > 0);
  if (priced.length) return priced.reduce((a, b) => (parseFloat(b.unit_price) < parseFloat(a.unit_price) ? b : a));
  return list[0];
}

// The Product Link a pick fills in: the supplier's link to the item, but only
// when the product has exactly one supplier. With several, which to buy from is
// the PMO's call, and the review lists them all.
export function catalogueProductUrl(catalogueSuppliers) {
  const list = Array.isArray(catalogueSuppliers) ? catalogueSuppliers : [];
  return list.length === 1 ? orderLink(list[0].how_to_order) : null;
}

// Returns the form fields to overwrite. Supplier fields are only returned when
// the catalogue actually names a supplier, so a supplier somebody has already
// typed is left alone when the picked item has none, and product_url only when
// the catalogue has a link for it.
export function prefillFromCatalogue(item, catalogueSuppliers, suppliers) {
  const chosen = chooseCatalogueSupplier(catalogueSuppliers);
  const unit_cost = money(chosen?.unit_price) || money(item?.unit_cost);
  if (!chosen || !String(chosen.supplier_name || '').trim()) return { unit_cost };

  const product_url = catalogueProductUrl(catalogueSuppliers);
  const link = product_url ? { product_url } : {};
  const wanted = normaliseName(chosen.supplier_name);
  const match = (suppliers || []).find(s => s.is_active !== 0 && normaliseName(s.name) === wanted);
  return match
    ? { unit_cost, supplier_id: match.id, supplier_name_free: '', ...link }
    : { unit_cost, supplier_id: '', supplier_name_free: String(chosen.supplier_name).trim(), ...link };
}
